<template>
  <div class="relative my-4 rounded-md border border-gray-300">
    <autoresizing-text-area
      id="note-input"
      v-model="inputText"
      class="autoresizing-text-area -mb-7 max-h-100 w-full overflow-y-auto rounded-md border-0 border-transparent pt-9 text-sm text-gray-700 focus-within:outline-hidden focus:border-transparent focus:ring-0 focus:outline-hidden"
      :class="[{ 'min-h-[100px] bg-white': isDealActivityPipModeActivated }]"
      :placeholder="$t('DEALS.NOTES.ADD_NOTE')"
      :rows="3"
      :auto-focus="autoFocus"
      data-cy="deal-note-input"
      @keydown.enter="addNote"
    />
    <div
      class="relative mt-1 flex items-center justify-between border-t border-gray-300 bg-gray-100 p-1"
    >
      <div class="flex flex-wrap items-center gap-2">
        <note-type-selector
          v-if="!disableTypeSelector"
          :note-type="noteType"
          :disabled="!!inputId || disableTypeSelector"
          @type:update="$emit('type:update', $event)"
        />
        <notes-dropdown
          v-if="noteType === NOTES_TYPES.APPLICATION_NOTE"
          v-model="selectedStatus"
          :options="dropdownOptions.options"
          :colors-for-options="dropdownOptions.colors"
          :active-flow-index="activeFlowIndex"
          :style="dropdownStyle"
        />
        <notes-dropdown
          v-if="noteType === NOTES_TYPES.CALL_NOTE"
          v-model="selectedDisposition"
          :options="callStatuses"
          :colors-for-options="COLORS_BY_DISPOSITION_NOTE_STATUS"
          :placeholder="$t('ACTIVITY.SELECT_CALL_DISPOSITION')"
          class="min-w-50"
          :style="dropdownStyle"
        />
      </div>

      <div
        class="flex h-max items-center"
        :class="{
          'w-full justify-end p-1': noteType === NOTES_TYPES.BUSINESS_NOTE,
        }"
      >
        <button
          v-if="noteType === NOTES_TYPES.APPLICATION_NOTE"
          v-tooltip="{
            content: $t('DEALS.DEAL_NOTES.VISIBLE_IN_DEALS_LIST'),
            onShow: () => !isDealActivityPipModeActivated,
          }"
          data-cy="visible-in-deals-list"
          class="flex cursor-pointer items-center justify-center rounded-md p-1 py-3"
          @click.prevent.stop="toggleVisibleInDealsList"
        >
          <icon-base
            :icon="IconListView"
            height="20"
            width="20"
            view-box="0 0 24 24"
            class="mx-1 cursor-pointer"
            :class="visibleInDealsList ? 'text-primary' : 'text-gray-400'"
            @click.stop="toggleVisibleInDealsList"
          />
        </button>
        <note-visibility-button
          v-if="noteType !== NoteType.Business"
          :value="visibilityScope"
          @change-scope="setVisibilityScope"
        />
        <business-notes-visibility-button
          v-else
          :visibility-scope="visibilityScope"
          @change-scope="setVisibilityScope"
        />
        <button
          class="flex h-[36px] w-10 items-center justify-center rounded-md p-1"
          :class="
            inputText
              ? 'cursor-pointer bg-primary'
              : 'cursor-not-allowed bg-gray-300'
          "
          @click="addNote()"
        >
          <icon-base
            height="20"
            width="20"
            :icon="IconSubmit"
            icon-color="white"
            class="ml-1"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";
import { storeToRefs } from "pinia";

import { NoteType } from "@/enums/dealActivity";
import { STATUS_COLOR_CODES } from "@/helpers/constants";
import { NOTES_TYPES } from "@/helpers/constants";
import { COLORS_BY_DISPOSITION_NOTE_STATUS } from "@/helpers/constants/colors";
import { WORKFLOW_TO_APP_STATUS } from "@/helpers/constants/workflow";
import { useAuth } from "@/hooks/auth";
import { useDealsBase } from "@/hooks/deals";
import {
  useCreateNoteMutation,
  useUpdateNoteMutation,
} from "@/hooks/queries/useInbox";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";
import useNotesStore from "@/stores/notes";
import usePiPStore from "@/stores/pip";

import NoteTypeSelector from "@/components/deals/NoteTypeSelector.vue";
import IconListView from "@/components/icons/IconListView.vue";
import IconSubmit from "@/components/icons/IconSubmit.vue";
import IconBase from "@/components/ui/IconBase.vue";
import NotesDropdown, { type Option } from "@/components/ui/NotesDropdown.vue";
import AutoresizingTextArea from "@/views/deals/components/dealNotes/AutoresizingTextArea.vue";
import BusinessNotesVisibilityButton from "@/views/deals/components/dealNotes/BusinessNotesVisibilityButton.vue";
import NoteVisibilityButton from "@/views/deals/components/dealNotes/NoteVisibilityButton.vue";

import type { ApplicationStatus } from "@/models/applications";
import type { NoteVisibilityScope } from "@/models/common";
import type { Note, NotePayload } from "@/models/notes";
import type { IFlowRecord, IWorkflowStage } from "@/models/workflows";

const {
  noteToEdit,
  noteType = NoteType.Application,
  disableTypeSelector,
  isAllActivity = false,
  autoFocus = true,
} = defineProps<{
  noteToEdit?: Note | null;
  noteType?: NoteType;
  disableTypeSelector?: boolean;
  isAllActivity?: boolean;
  autoFocus?: boolean;
}>();

const emit = defineEmits<{
  "edit-note:unset": [];
  "note:edited": [boolean];
  "type:update": [NoteType];
}>();

const dropdownStyle = {
  buttonHeight: "h-6",
  optionHoverBgColor: "hover:bg-primary-background-active",
};

const { getters } = useStore();
const { isLendflowUser } = useAuth();
const { activeDeal } = useDealsBase();
const notesStore = useNotesStore();
const { isDealActivityPipModeActivated } = storeToRefs(notesStore);
const pipStore = usePiPStore();
const { activePiPFlows, activePiPStages, activePiPWorkflow } =
  storeToRefs(pipStore);

const { mutateAsync: updateNoteMutation } =
  useUpdateNoteMutation(isAllActivity);
const { mutateAsync: createNoteMutation } =
  useCreateNoteMutation(isAllActivity);

const { activeTemplate } = useActiveWorkflowTemplate();

const flows = computed<IFlowRecord[]>(() =>
  isEmpty(activePiPFlows.value)
    ? getters["workflows/activeTemplateFlow"]
    : activePiPFlows.value
);

const stages = computed<IWorkflowStage[]>(() =>
  isEmpty(activePiPStages.value)
    ? getters["workflows/activeTemplateStages"]
    : activePiPStages.value
);

const callDispositions = computed<Record<string, string>>(
  () => getters["options/callDispositions"]
);

const callStatuses = computed(() =>
  Object.entries(callDispositions.value).map(([id, value]) => ({
    id,
    name: value,
  }))
);

const activeFlowIndex = computed<number>(() => {
  return flows.value.findIndex(
    (flow) => flow.stage_id === activeStage.value?.id
  );
});

const activeStage = computed<IWorkflowStage | undefined>(() => {
  const activeWorkflowStage = activeTemplate.value.content?.stages?.find(
    ({ id }) => activeTemplate.value.stage_id === id
  );
  if (activePiPWorkflow.value) {
    return (
      activePiPWorkflow.value.content?.stages?.find(
        ({ id }) => activePiPWorkflow.value?.stage_id === id
      ) ?? activeWorkflowStage
    );
  }

  return activeWorkflowStage;
});

interface DropdownOptions {
  options: Option[];
  colors: Record<string, number>;
}

const dropdownOptions = computed(() =>
  flows.value.reduce<DropdownOptions>(
    (final, { id, name, stage_id }) => {
      const stageType = stages.value.find((stage) => stage.id === stage_id)
        ?.type as keyof typeof WORKFLOW_TO_APP_STATUS;
      const statusCode = WORKFLOW_TO_APP_STATUS[stageType] || 0;
      const color = STATUS_COLOR_CODES[statusCode as ApplicationStatus];

      final.options.push({ id, name });
      final.colors[id] = color;
      return final;
    },
    { options: [], colors: {} }
  )
);

const defaultVisibility = computed(() =>
  isLendflowUser && noteType !== "business" ? 2 : 1
);

const inputText = ref("");
const visibilityScope = ref<NoteVisibilityScope>(defaultVisibility.value);
const visibleInDealsList = ref(
  noteToEdit?.visible_in_deal_list === undefined ||
    noteToEdit?.visible_in_deal_list
);
const selectedStatus = ref(noteToEdit?.stage);
const selectedDisposition = ref(noteToEdit?.call_disposition);

const inputId = computed(() => noteToEdit?.id);

const setVisibilityScope = (visibility: NoteVisibilityScope) => {
  visibilityScope.value = visibility;
};

const toggleVisibleInDealsList = () => {
  visibleInDealsList.value = !visibleInDealsList.value;
};

const addNote = async (event?: KeyboardEvent) => {
  const isMacOs = navigator.userAgent.toLowerCase().indexOf("mac os x") !== -1;
  const metaPressed = isMacOs ? event?.metaKey : event?.ctrlKey;
  const keyComboAccepted = !event || metaPressed;
  if (!keyComboAccepted || !inputText.value.length) {
    return;
  }
  event?.preventDefault();

  const payload: NotePayload = {
    notable_type: noteType,
    notable_id: getNotableId(noteType),
    comment: inputText.value,
    visibility_scope: visibilityScope.value,
  };
  if (noteType === NOTES_TYPES.APPLICATION_NOTE) {
    payload.stage = selectedStatus.value;
    payload.visible_in_deal_list = visibleInDealsList.value;
  }
  if (noteType === NOTES_TYPES.CALL_NOTE) {
    payload.call_disposition = selectedDisposition.value;
  }

  if (inputId.value) {
    payload.id = Number(inputId.value);
    emit("edit-note:unset");
  }
  inputId.value
    ? await updateNoteMutation(payload)
    : await createNoteMutation(payload);
  inputText.value = "";
  visibilityScope.value = defaultVisibility.value;
  emit("note:edited", !!payload.id);
};

const getNotableId = (noteType: NoteType) => {
  switch (noteType) {
    case NOTES_TYPES.BUSINESS_NOTE:
      return activeDeal.value.business?.id;
    case NOTES_TYPES.CALL_NOTE:
      return noteToEdit?.notable_id || "";
    default:
      return activeDeal.value?.id;
  }
};

watch(
  () => noteToEdit,
  () => {
    if (noteToEdit) {
      inputText.value = noteToEdit?.comment;
      visibilityScope.value =
        noteToEdit?.visibility_scope || defaultVisibility.value;
      selectedStatus.value = noteToEdit.stage;
      selectedDisposition.value = noteToEdit.call_disposition;
      visibleInDealsList.value =
        noteToEdit.visible_in_deal_list === undefined ||
        noteToEdit.visible_in_deal_list;
      return;
    }
    inputText.value = "";
    visibilityScope.value = defaultVisibility.value;
    selectedStatus.value = undefined;
    selectedDisposition.value = undefined;
    visibleInDealsList.value = true;
  }
);

watch([flows, activeFlowIndex], ([newFlows, newFlowIndex]) => {
  if (newFlows && dropdownOptions.value?.options.length) {
    selectedStatus.value = dropdownOptions.value.options[newFlowIndex].id;
  }
});

watch(
  () => noteType,
  () => {
    visibilityScope.value = defaultVisibility.value;
  }
);
</script>
